/**
 * The review bar's adapter (issue #147): the follow-up queue (oversikt/queue.ts) in, the bar's cases
 * out. The queue already decides what needs a manager across every open period; this only words each
 * point as a case, gives it its actions, and orders the categories as the design does. No markup.
 * Pure; tested in reviewBarAdapter.test.ts.
 */
import type { ApiShift, ApiUser } from '@/api/data';
import { T } from '@/i18n';
import { type Entry, duration, oslo, osloISO, today as todayOslo } from '@/rules';
import { DN, DNL, MONTHS, fullName, num } from '@/ui/format';
import { addDays, dow, isoWeek } from '@timefly/core';
import { isUnplanned, pauseOf } from '../oversikt/day';
import type { Group, Point, Queue } from '../oversikt/queue';
import type { ReviewItem } from './Panel';
import { toVaktplan } from './link';
import { typeLabel } from './model';
import type { WeekItem } from './review';
import {
  CATS,
  type CaseAction,
  type CaseCat,
  type CaseShift,
  type Fact,
  type ReviewCase,
  type SourceGap,
} from './reviewBar.types';

const CAT_OF: Record<Group, CaseCat> = {
  oppmote: 'na',
  foresporsler: 'foresporsler',
  timer: 'timer',
  plan: 'planlegg',
  // an employee number or agreed week missing stops the pay file, so it is a payroll case
  oppsett: 'lonn',
};
const TONE: Record<Point['tone'], ReviewCase['tone']> = { dgr: 'd', warn: 'w', neu: 'i' };
const day = (d: string) =>
  `${Number(d.slice(8, 10))}. ${(MONTHS[Number(d.slice(5, 7)) - 1] ?? '').slice(0, 3)}.`;
const clock = (iso: string | null | undefined) => {
  const o = oslo(iso);
  return o ? T('{day} kl. {time}', { day: day(o.date), time: o.time }) : '';
};

export interface PayMonthState {
  /** YYYY-MM */
  ym: string;
  state: 'open' | 'exported' | 'closed';
}

export interface CaseInput {
  queue: Queue;
  /** shifts the queue read, to give a single case its recorded times */
  shifts: ApiShift[];
  users: ApiUser[];
  /** last month's pay month, for whoever may see pay; null when not read or not allowed */
  payMonth: PayMonthState | null;
  /**
   * The period on screen, item by item (review.ts reviewOf): staffing gaps, rule and attendance
   * entries, availability. What the company-wide queue already holds (requests, absences, an open
   * punch) is not repeated. `thisWeek`: the window includes today's week, so the queue's worklist of
   * this week's breaches is the same entries said twice and is left out.
   */
  week?: { items: WeekItem[]; thisWeek: boolean; shifts?: ApiShift[] } | undefined;
  /** may answer absence requests (fravaer.godkjenne); without it the case only links to Fravær */
  decideAbsence?: boolean | undefined;
  /** today in Oslo and the time now, for "I dag" and a clock still running; the real ones when absent */
  today?: string | undefined;
  now?: number | undefined;
}
type Ctx = Pick<CaseInput, 'shifts' | 'users' | 'decideAbsence' | 'today' | 'now'>;

// ---- the template's parts (prototype case-templates, owner 2026-10-28) ----

const H = 36e5;
const ms = (iso: string) => Date.parse(iso);
const dateOf = (s: ApiShift) => oslo(s.start_time)?.date ?? '';
const hm = (iso: string | null | undefined) => oslo(iso)?.time ?? '';
const round1 = (n: number) => Math.round(n * 10) / 10;
/** "Lør 31. okt." */
const shortDay = (d: string) => `${DN[dow(d)]} ${day(d)}`;
/** "onsdag 28. oktober", with "I dag, " in front for today */
function longDay(d: string, today: string, capital = true): string {
  const s = `${DNL[dow(d)]} ${Number(d.slice(8, 10))}. ${MONTHS[Number(d.slice(5, 7)) - 1]}`;
  if (d === today) return T('I dag, {day}', { day: s });
  return capital ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
/** "2. til 4. november", or one day in full */
function spanText(from: string, to: string, today: string): string {
  if (from === to) return longDay(from, today);
  const m = (d: string) => MONTHS[Number(d.slice(5, 7)) - 1];
  const a =
    from.slice(0, 7) === to.slice(0, 7)
      ? `${Number(from.slice(8, 10))}.`
      : `${Number(from.slice(8, 10))}. ${m(from)}`;
  return T('{a} til {b}', { a, b: `${Number(to.slice(8, 10))}. ${m(to)}` });
}
/** A shift as the template's chip: time, type, its type's colour. */
export function chipOf(s: ApiShift): CaseShift {
  return {
    time: `${hm(s.start_time)}–${hm(s.end_time)}`,
    type: typeLabel(s.shiftType) ?? '',
    color: s.shiftType?.color_code ?? null,
    day: shortDay(dateOf(s)),
  };
}
const firstName = (u: { name: string } | null | undefined) => u?.name.split(' ')[0] || T('Den ansatte');
/** Ring or Send melding, only when the person has a phone the reader may see. */
function contact(u: ApiUser | undefined, how: 'tel' | 'sms'): CaseAction[] {
  const phone = u?.phone?.replace(/\s/g, '');
  if (!phone) return [];
  return [
    {
      kind: 'contact',
      href: `${how}:${phone}`,
      label: how === 'tel' ? T('Ring {name}', { name: firstName(u) }) : T('Send melding'),
    },
  ];
}
const unique = (shifts: ApiShift[]) => [...new Map(shifts.map((s) => [s.id, s])).values()];
function actionsOf(p: Point, shift: ApiShift | undefined, decideAbsence: boolean): CaseAction[] {
  // a deleted type is never granted as it is (the server refuses, 2026-10-16): change it or decline
  if (p.absence && decideAbsence && !p.absence.typeActive)
    return [
      { kind: 'link', to: p.to, label: T('Bytt type i Fravær') },
      {
        kind: 'decide',
        what: 'absence',
        id: p.absence.id,
        ok: false,
        label: T('Avslå'),
        consequence: T('Den ansatte får beskjed om at det er avslått.'),
      },
    ];
  if (p.absence && decideAbsence)
    return [
      {
        kind: 'decide',
        what: 'absence',
        id: p.absence.id,
        ok: true,
        label: T('Innvilg'),
        consequence: T('Den ansatte får beskjed når du har svart.'),
      },
      {
        kind: 'decide',
        what: 'absence',
        id: p.absence.id,
        ok: false,
        label: T('Avslå'),
        consequence: T('Den ansatte får beskjed om at det er avslått.'),
      },
    ];
  if (p.decision) {
    const what = p.decision.kind;
    return [
      {
        kind: 'decide',
        what,
        id: p.decision.id,
        ok: true,
        label: what === 'swap' ? T('Godkjenn byttet') : T('Godkjenn'),
        consequence: T('De ansatte får beskjed når du har svart.'),
      },
      {
        kind: 'decide',
        what,
        id: p.decision.id,
        ok: false,
        label: T('Avslå'),
        consequence: T('De ansatte får beskjed om at det er avslått.'),
      },
    ];
  }
  // a day row (no clock-in, late, early): the shift itself, in the side panel, not Oversikt (owner 18.10)
  if (p.key.startsWith('day') && shift) return [{ kind: 'shift', shiftId: shift.id, label: T(p.label) }];
  return [{ kind: 'link', to: p.to, label: T(p.label) }];
}

function factsOf(p: Point, shift: ApiShift | undefined): Fact[] {
  if (p.key.startsWith('openpunch') && shift)
    return [
      { label: T('Stemplet inn'), value: clock(shift.punch_in_time) },
      { label: T('Planlagt slutt'), value: clock(shift.end_time) },
      { label: T('Registrert slutt'), value: T('Mangler') },
    ];
  const out: Fact[] = [];
  // the queue's meta line: when it applies, then what it is ("Mottatt 3. okt", "Arbeidsliste")
  for (const part of p.meta.split(' · ').slice(1)) {
    const m = /^Mottatt (.+)$/.exec(part);
    if (m) out.push({ label: T('Mottatt'), value: m[1] as string });
  }
  if (p.records != null) out.push({ label: T('I listen'), value: String(p.records) });
  return out;
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The title without the name the context line already says (review of PR #150): "Mangler utstempling". */
function withoutName(title: string, name: string): string {
  if (!name || !title.startsWith(name)) return title;
  const rest = title.slice(name.length).replace(/^\s*·?\s*/, '');
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : title;
}

type Template = Pick<
  ReviewCase,
  'what' | 'dateText' | 'shift' | 'fig' | 'why' | 'law' | 'status' | 'outcome' | 'balance'
> & {
  title?: string;
  actions?: CaseAction[];
  facts?: Fact[];
};
const LONN = 'Lønn';
/**
 * The state beside the dot, said as it is (owner's review 2026-10-28): who the case waits on, or
 * that a registration must be put right, or that it is ready; «Brudd» only for a rule broken.
 */
export const STATE = {
  you: 'Venter på deg',
  employee: 'Venter på ansatt',
  fix: 'Må avklares',
  ready: 'Klar til godkjenning',
  breach: 'Regelvarsel',
} as const;
const fact = (label: string, value: string): Fact => ({ label, value });
/** "fre 30. okt. 14:30–22:30 Kitchen closing": one shift in words */
const shiftWords = (s: ApiShift) => {
  const c = chipOf(s);
  return `${(c.day ?? '').toLowerCase()} ${c.time}${c.type ? ` ${c.type}` : ''}`;
};

/** The template's parts for a queue point: state, what, the decisive facts, why, and its actions. */
function pointTemplate(p: Point, shift: ApiShift | undefined, user: ApiUser | undefined, x: Ctx): Template {
  const today = x.today ?? todayOslo();
  const now = x.now ?? Date.now();
  const name = firstName(user ?? shift?.user);
  const when = p.records == null && p.date ? { dateText: longDay(p.date, today) } : {};
  const at = shift ? { ...when, shift: chipOf(shift) } : when;
  // the clock still running after the shift ended: one case, «Klokken går», however the queue found it
  if (p.key.startsWith('openpunch') && shift?.punch_in_time)
    return {
      ...at,
      status: T(STATE.fix),
      title: T('Klokken går'),
      what: T('{name} har ikke stemplet ut', { name }),
      facts: [
        fact(T('Stemplet inn'), clock(shift.punch_in_time)),
        fact(T('Planlagt slutt'), clock(shift.end_time)),
        fact(T('Utstempling'), T('Mangler')),
      ],
      why: T('Vakten er over. Uten sluttid kan timene ikke godkjennes.'),
      law: T(LONN),
      actions: [
        { kind: 'correct-out', shiftId: shift.id, label: T('Fyll inn utstempling') },
        ...contact(user, 'tel'),
      ],
    };
  if (p.key.startsWith('day') && shift) {
    if (!shift.punch_in_time)
      return {
        ...at,
        status: T(STATE.fix),
        what: T('Planlagt start {start}, ingen innstempling kl. {now}', {
          start: hm(shift.start_time),
          now: hm(new Date(now).toISOString()),
        }),
        why: T('Uten innstempling kommer ikke timene med i lønnsgrunnlaget.'),
        law: T(LONN),
        actions: [
          { kind: 'correct-in', shiftId: shift.id, label: T('Sett inntid') },
          ...contact(user, 'tel'),
        ],
      };
    // approval needs both punches (PUT accept refuses without); while the clock runs, the shift opens
    const settle: CaseAction = shift.punch_out_time
      ? { kind: 'approve', shiftIds: [shift.id], label: T('Godkjenn som stemplet') }
      : { kind: 'shift', shiftId: shift.id, label: T('Rett registreringen') };
    const registered = `${hm(shift.punch_in_time)}–${shift.punch_out_time ? hm(shift.punch_out_time) : T('pågår')}`;
    if (isUnplanned(shift))
      return {
        ...when,
        status: T(STATE.you),
        what: T('{name} er registrert uten planlagt vakt', { name }),
        facts: [fact(T('Registrert'), registered)],
        why: T('Appen lagde en vakt da {name} stemplet. Er registreringen riktig?', { name }),
        law: T(LONN),
        actions: shift.punch_out_time
          ? [settle, { kind: 'shift', shiftId: shift.id, label: T('Rett registreringen') }]
          : [settle, ...contact(user, 'sms')],
      };
    const late = Math.round((ms(shift.punch_in_time) - ms(shift.start_time)) / 60_000);
    return {
      ...when,
      status: T(STATE.you),
      what: T('Stemplet inn {n} min etter planlagt start', { n: late }),
      facts: [
        fact(T('Planlagt'), `${hm(shift.start_time)}–${hm(shift.end_time)}`),
        fact(T('Registrert'), registered),
      ],
      why: T('Timene regnes fra innstemplingen. Rett tiden hvis {name} begynte før.', { name }),
      law: T(LONN),
      actions: [
        settle,
        ...(shift.punch_out_time
          ? [{ kind: 'shift', shiftId: shift.id, label: T('Rett registreringen') } as CaseAction]
          : contact(user, 'sms')),
      ],
    };
  }
  const a = p.absence;
  if (a) {
    const from = oslo(a.start)?.date ?? p.date;
    const to = oslo(a.end)?.date ?? from;
    // the shifts in the period stay on the person until someone moves them: affected, not removed
    const hit = (s: ApiShift) =>
      a.kind === 'date'
        ? dateOf(s) >= from && dateOf(s) <= to
        : ms(s.start_time) < ms(a.end) && ms(s.end_time) > ms(a.start);
    const affected = unique(x.shifts).filter((s) => s.user_id === p.userId && hit(s)).length;
    if (!a.typeActive)
      return {
        dateText: spanText(from, to, today),
        status: T(STATE.fix),
        what: T('Fraværstypen {type} er slettet', { type: a.type }),
        why: T('En slettet type kan ikke innvilges. Bytt type, eller avslå.'),
      };
    return {
      dateText: spanText(from, to, today),
      status: T(STATE.you),
      what: T('{name} ber om {type}', { name, type: a.type.toLowerCase() }),
      ...(affected
        ? {
            facts: [
              fact(
                T('Vakter i perioden'),
                affected === 1 ? T('1 vakt berørt') : T('{n} vakter berørt', { n: affected }),
              ),
            ],
          }
        : {}),
      why: T('Den ansatte får beskjed når du har svart.'),
    };
  }
  // an approved absence the employee wants cancelled (owner review 2026-10-28): it stands until the
  // answer; person and period said once; the balance only as the record says it, never assumed accepted
  const cx = p.cancel;
  if (p.key.startsWith('cancel') && cx) {
    const n = Math.round((Date.parse(cx.to) - Date.parse(cx.from)) / 864e5) + 1;
    const type = cx.type.toLowerCase();
    const thisYear = Number(today.slice(0, 4));
    const year = cx.from.slice(0, 4) === cx.to.slice(0, 4) ? Number(cx.from.slice(0, 4)) : null;
    const next = cx.chargedNext ?? 0;
    const back =
      cx.charged == null ? null : year === thisYear ? cx.charged - next : year === thisYear + 1 ? next : null;
    return {
      status: T('Venter på svar'),
      dateText: `${spanText(cx.from, cx.to, today)} (${n === 1 ? T('1 dag') : T('{n} dager', { n })})`,
      what: T('Ber om å avlyse {type}', { type }),
      outcome: [
        ...(cx.deductible && cx.charged
          ? [cx.charged === 1 ? T('1 feriedag tilbake') : T('{n} feriedager tilbake', { n: num(cx.charged) })]
          : []),
        T('Ingen vakter opprettes automatisk.'),
      ],
      ...(cx.deductible && back && p.userId != null && year != null
        ? {
            balance: {
              userId: p.userId,
              field: year === thisYear ? 'vacation_days_balance' : 'next_year_vacation_days_balance',
              back,
            },
          }
        : {}),
      why: type === 'ferie' ? T('Ferien står til du svarer.') : T('Fraværet står til du svarer.'),
      actions: [
        { kind: 'link', to: p.to, label: T('Vurder avlysning i Fravær') },
        { kind: 'link', to: toVaktplan({ week: cx.from }), label: T('Se perioden i planen') },
      ],
    };
  }
  const o = p.offer;
  if (o) {
    const claim = o.offered_by === o.accepted_by;
    const taker = fullName(o.acceptedByUser);
    return {
      ...when,
      status: T(STATE.you),
      what: claim
        ? T('{name} vil ta den ledige vakten', { name: taker })
        : T('{name} vil gi vakten til {to}', { name: fullName(o.offeredByUser), to: taker }),
      facts: [
        ...(o.shift ? [fact(T('Vakt'), shiftWords(o.shift))] : []),
        ...(claim ? [] : [fact(T('Svar'), T('{name} har sagt ja', { name: firstName(o.acceptedByUser) }))]),
      ],
      outcome: [T('Ved godkjenning får {name} vakten.', { name: firstName(o.acceptedByUser) })],
      why: claim
        ? T('{name} får beskjed når du svarer.', { name: firstName(o.acceptedByUser) })
        : T('Begge får beskjed når du svarer.'),
    };
  }
  const sw = p.swap;
  if (sw) {
    const one = firstName(sw.offeredByUserOne);
    const two = firstName(sw.offeredByUserTwo);
    return {
      ...when,
      status: T(STATE.you),
      what: T('{one} og {two} vil bytte vakt', {
        one: fullName(sw.offeredByUserOne),
        two: fullName(sw.offeredByUserTwo),
      }),
      facts: [fact(T('Svar'), T('Begge har sagt ja'))],
      outcome: [
        ...(sw.shiftTwo ? [T('{name} får {shift}', { name: one, shift: shiftWords(sw.shiftTwo) })] : []),
        ...(sw.shiftOne ? [T('{name} får {shift}', { name: two, shift: shiftWords(sw.shiftOne) })] : []),
      ],
      why: T('Begge får beskjed når du svarer.'),
    };
  }
  if (p.key.startsWith('offered') && shift)
    return {
      ...at,
      status: T(STATE.employee),
      what: T('{name} tilbyr bort vakten', { name }),
      why: T('Vakten er fortsatt {name} sin til noen tar den og du har godkjent.', { name }),
    };
  // who the other side is, is not sent (trenger nye data): words only
  if (p.key.startsWith('swapreq'))
    return {
      ...at,
      status: T(STATE.employee),
      what: T('{name} vil bytte vakt', { name }),
      why: T('Venter på at den andre ansatte sier ja.'),
    };
  if (p.key.startsWith('cover') && shift)
    return {
      ...when,
      status: T(STATE.you),
      what: T('Vakten {shift} har ingen som dekker den', { shift: shiftWords(shift) }),
      why: T('{name} har godkjent fravær i vakten.', { name }),
      actions: [
        {
          kind: 'link',
          to: toVaktplan({ day: p.date }),
          label: T('Se {day} i planen', { day: shortDay(p.date).toLowerCase() }),
        },
      ],
    };
  // the worklists: their rows are not in the case today (trenger nye data), so words only
  const lists: Record<string, Template> = {
    'time-missing': {
      status: T(STATE.fix),
      why: T('Uten registrerte tider kommer timene ikke med i lønnsgrunnlaget.'),
      law: T(LONN),
    },
    'time-deviation': { status: T(STATE.you), why: T('Se over tidene før de godkjennes.'), law: T(LONN) },
    'time-ready': {
      status: T(STATE.ready),
      why: T('Stemplet innenfor toleransen. Bare godkjente timer går til lønn.'),
      law: T(LONN),
    },
    'time-prev-month': {
      status: T(STATE.you),
      why: T('Timer som ikke er godkjent, tas ikke med i godkjent lønnsgrunnlag.'),
      law: T(LONN),
    },
    open: { status: T(STATE.you), why: T('Vakter uten ansatt blir ikke dekket.') },
    drafts: { status: T(STATE.you), why: T('De ansatte ser ikke vaktene før du publiserer.') },
    breaches: {
      status: T(STATE.breach),
      why: T('Hver av dem er en egen sak når du ser denne uken i planen.'),
      law: T('AML kap. 10 og 11'),
    },
    setup: {
      status: T(STATE.fix),
      why: T('Lønn og regelsjekk bruker disse opplysningene.'),
      law: T(LONN),
      ...(p.gaps?.length ? { facts: p.gaps.map((g) => fact(g.name, g.missing.join(', '))) } : {}),
    },
  };
  const list = lists[p.key];
  if (!list) return {};
  const LIST_LINK: Record<string, string> = {
    'time-missing': T('Se vakter uten stempling i Timeliste'),
    'time-deviation': T('Se avvikene i Timeliste'),
    'time-ready': T('Se vaktene i Timeliste'),
    'time-prev-month': T('Se forrige måned i Timeliste'),
  };
  const toList: CaseAction = { kind: 'link', to: p.to, label: LIST_LINK[p.key] ?? T('Åpne arbeidslisten') };
  if (p.key === 'time-ready' && p.shiftIds?.length) {
    // the batch in hours as clocked, from the shifts the queue read: in to out, less the clocked break
    const ids = new Set(p.shiftIds);
    const hours = unique(x.shifts)
      .filter((s) => ids.has(s.id) && s.punch_in_time && s.punch_out_time)
      .reduce(
        (sum, s) =>
          sum +
          (ms(s.punch_out_time as string) - ms(s.punch_in_time as string)) / H -
          (pauseOf(s, now) ?? 0) / 60,
        0,
      );
    return {
      ...list,
      facts: [fact(T('Stemplet'), T('{h} t', { h: num(round1(hours)) }))],
      actions: [
        { kind: 'approve', shiftIds: p.shiftIds, label: T('Godkjenn alle {n}', { n: p.shiftIds.length }) },
        toList,
      ],
    };
  }
  return p.key.startsWith('time-') ? { ...list, actions: [toList] } : list;
}

/** One point as a case: its words, facts and actions. */
export function caseOf(p: Point, x: Ctx): ReviewCase {
  const shift = p.shiftId != null ? x.shifts.find((s) => s.id === p.shiftId) : undefined;
  const user = p.userId != null ? x.users.find((u) => u.id === p.userId) : undefined;
  const t = pointTemplate(p, shift, user, x);
  const correct =
    shift && t.actions?.some((a) => a.kind === 'correct-out' || a.kind === 'correct-in')
      ? {
          shiftId: shift.id,
          punchIn: shift.punch_in_time,
          plannedStart: shift.start_time,
          plannedEnd: shift.end_time,
          dayLabel: day(oslo(shift.punch_in_time ?? shift.start_time)?.date ?? p.date),
        }
      : undefined;
  const { actions, title, facts: _f, ...parts } = t;
  return {
    ...parts,
    key: p.key,
    cat: CAT_OF[p.group],
    tone: TONE[p.tone],
    person: user ? { id: user.id, name: fullName(user) } : undefined,
    date: p.records == null ? p.date : undefined,
    dateLabel: p.records == null ? day(p.date) : undefined,
    when: p.meta.split(' · ')[0] ?? '',
    // an absence request: what and when as the title, the rest of the queue's line below it (F5)
    title:
      title ??
      (p.absence
        ? p.sub.split(' · ').slice(0, 2).join(' · ')
        : user
          ? withoutName(p.title, fullName(user))
          : p.title),
    description: p.absence
      ? p.absence.typeActive
        ? capital(p.sub.split(' · ').slice(2).join(' · '))
        : T(
            'Fraværstypen er slettet, så søknaden kan ikke innvilges slik den er. Bytt til en aktiv type i Fravær, eller avslå.',
          )
      : p.sub,
    facts: t.facts ?? factsOf(p, shift),
    actions: actions ?? actionsOf(p, shift, !!x.decideAbsence),
    correct,
    shiftId: p.shiftId,
    span: shift ? { start: shift.start_time, end: shift.end_time } : undefined,
    records: p.records,
    absence: p.absence,
  };
}

/** Last month left open in Lønn: one case, to the month in Lønn. */
function payCase(m: PayMonthState): ReviewCase | null {
  // only what the server said: an answer without a state is not "not sent"
  if (m.state !== 'open' && m.state !== 'exported') return null;
  const name = MONTHS[Number(m.ym.slice(5, 7)) - 1] ?? m.ym;
  const Name = name.charAt(0).toUpperCase() + name.slice(1);
  return {
    key: `pay${m.ym}`,
    cat: 'lonn',
    tone: 'w',
    when: T('Tidligere'),
    title:
      m.state === 'exported'
        ? T('{m} er eksportert, men ikke lukket', { m: Name })
        : T('{m} er ikke sendt til lønn', { m: Name }),
    description:
      m.state === 'exported'
        ? T('Lukk måneden når lønnen er kjørt, så kan ingen endre timene etterpå uten å åpne den igjen.')
        : T('Godkjente timer i {m} er ikke eksportert eller sendt til lønnssystemet.', { m: name }),
    facts: [],
    // only the state the server keeps: whether hours were approved, received or paid is not known here
    status: T(STATE.you),
    what:
      m.state === 'exported'
        ? T('{m} er eksportert, måneden er åpen', { m: Name })
        : T('{m} er ikke eksportert til lønn', { m: Name }),
    dateText: `${Name} ${m.ym.slice(0, 4)}`,
    why:
      m.state === 'exported'
        ? T('Lukk måneden når lønnen er kjørt. Den kan åpnes igjen om noe må endres.')
        : T('Godkjente timer for {m} er ikke eksportert.', { m: name }),
    law: T('Lønn'),
    actions: [{ kind: 'link', to: `/lonn?mnd=${m.ym}`, label: T('Gå til lønn for {m}', { m: name }) }],
    shiftId: undefined,
    records: undefined,
  };
}

const WEEK_CAT: Partial<Record<ReviewItem['group'], CaseCat>> = {
  staff: 'planlegg',
  rules: 'planlegg',
  avail: 'planlegg',
};
/** One item of the period on screen as a case; its way in is the item's own (review.ts). */
/** Minutes of [a, b) inside [from, to), all hours past one midnight. */
const overlapMin = (a: number, b: number, from: number, to: number) =>
  Math.max(0, Math.round((Math.min(b, to) - Math.max(a, from)) * 60));
/** A shift's hours past its start day's midnight in Oslo: 16:00 to 23:30 is 16 to 23.5, a night past 24. */
function hoursOf(s: ApiShift, punched: boolean): [number, number] | null {
  const a = punched ? s.punch_in_time : s.start_time;
  const b = punched ? s.punch_out_time : s.end_time;
  if (!a || !b) return null;
  const midnight = ms(osloISO(oslo(a)?.date ?? '', '00:00'));
  return [(ms(a) - midnight) / H, (ms(b) - midnight) / H];
}
/** The youth groups' night windows (backend services/payrollMath.js YOUTH), and the law for each. */
const YOUTH = {
  teen: { nightFrom: 23, eveningFrom: 21, law: 'AML § 11-3 (2)' },
  child: { nightFrom: 20, eveningFrom: undefined, law: 'AML § 11-3 (1)' },
};
/** One number against its limit, by rule code: the value and the limit from the entry's own figures. */
const n0 = (v: string | number | undefined) => Number(v) || 0;
const METER: Record<
  string,
  (a: Entry['args']) => { value: number; limit: number; week?: boolean; over?: boolean }
> = {
  weekly_over: (a) => ({ value: n0(a.max) + n0(a.hours), limit: n0(a.max), week: true }),
  weekly_over_48: (a) => ({ value: n0(a.hours), limit: 48, week: true }),
  youth_week_over: (a) => ({ value: n0(a.hours), limit: n0(a.max), week: true }),
  weekly_over_cap: (a) => ({ value: n0(a.hours), limit: 10, over: true }),
  weekly_over_max: (a) => ({ value: n0(a.hours), limit: 20, over: true }),
  year_over_200: (a) => ({ value: n0(a.hours), limit: 200, over: true }),
  day_over: (a) => ({ value: n0(a.hours), limit: n0(a.max) }),
  day_over_max: (a) => ({ value: n0(a.hours), limit: 9 }),
  day_over_13: (a) => ({ value: n0(a.hours), limit: 13 }),
  youth_day_over: (a) => ({ value: n0(a.hours), limit: n0(a.max) }),
};

/** Hours of the need in a row where fewer are planned than needed, with the fewest at once and the need. */
function shortStretches(cols: { h: number; have: number; need: number }[]) {
  const out: { from: number; to: number; have: number; need: number }[] = [];
  for (const c of cols) {
    if (c.have >= c.need) continue;
    const last = out.at(-1);
    if (last && last.to === c.h && last.have === c.have && last.need === c.need) last.to = c.h + 1;
    else out.push({ from: c.h, to: c.h + 1, have: c.have, need: c.need });
  }
  return out;
}

/** The template's parts for an item of the period on screen; the rule's own figures drawn. */
function weekTemplate(
  it: WeekItem,
  shift: ApiShift | undefined,
  user: ApiUser | undefined,
  shifts: ApiShift[],
  today: string,
): Template {
  const name = firstName(user ?? shift?.user);
  const d = it.day ?? (shift ? dateOf(shift) : undefined);
  const at = { ...(d ? { dateText: longDay(d, today) } : {}), ...(shift ? { shift: chipOf(shift) } : {}) };
  const weekday = d ? (DNL[dow(d)] ?? '') : '';
  const dayShort = d ? shortDay(d).toLowerCase() : '';
  const daySeen: CaseAction[] = d
    ? [{ kind: 'link', to: toVaktplan({ day: d }), label: T('Se {day} i planen', { day: dayShort }) }]
    : [];
  if (it.group === 'staff' && it.staff?.length && it.hours && d) {
    const Day = capital(DNL[dow(d)] ?? '');
    const short = round1(it.hours.need - it.hours.planned);
    return {
      ...at,
      what:
        short > 0
          ? short === 1
            ? T('{day} mangler 1 time bemanning', { day: Day })
            : T('{day} mangler {h} timer bemanning', { day: Day, h: num(short) })
          : T('{day} er under behovet i {n} timer', {
              day: Day,
              n: it.staff.filter((c) => c.have < c.need).length,
            }),
      status: T(STATE.you),
      fig: { kind: 'need', cols: it.staff },
      facts: shortStretches(it.staff).map((g) =>
        fact(
          `${String(g.from).padStart(2, '0')}:00–${String(g.to % 24).padStart(2, '0')}:00`,
          T('{n} av {m} på jobb', { n: g.have, m: g.need }),
        ),
      ),
      // enough hours in all, but not at the same time: the hours short, as the review says them
      why:
        short > 0
          ? T('{p} timer er planlagt mot et behov på {n}.', {
              p: num(round1(it.hours.planned)),
              n: num(it.hours.need),
            })
          : capital(it.text),
    };
  }
  if (it.group === 'avail' && shift)
    return {
      ...at,
      status: T(STATE.you),
      what: T('{name} er satt opp {day}, men har meldt at de ikke kan', { name, day: weekday }),
      why: T('Vakten står på {name} til du endrer den. Det er ikke registrert fravær.', { name }),
      actions: [{ kind: 'open', item: it.key, label: T('Åpne vakten {day}', { day: dayShort }) }, ...daySeen],
    };
  const e = it.entry ?? it.rule;
  if (!e) return at;
  const ruleSaid = capital(it.text);
  const actual = e.label === 'Actual';
  // «Brudd» only for a rule broken; a warning waits on the manager
  const status = { status: it.sev === 'd' ? T(STATE.breach) : T(STATE.you) };
  const openShift: CaseAction = {
    kind: 'open',
    item: it.key,
    label: T('Åpne vakten {day}', { day: dayShort }),
  };
  if (e.week) {
    const sunday = addDays(e.week, 6);
    Object.assign(at, {
      dateText: T('Uke {n}, {span}', { n: isoWeek(e.week), span: spanText(e.week, sunday, today) }),
    });
  }
  const meter = METER[e.code]?.(e.args);
  if (meter) {
    const h = num(round1(meter.value));
    const wk = e.week ? isoWeek(e.week) : '';
    return {
      ...at,
      what: meter.over
        ? T('{name} har {h} timer overtid', { name, h })
        : meter.week
          ? actual
            ? T('{name} har jobbet {h} timer i uke {n}', { name, h, n: wk })
            : T('{name} er planlagt {h} timer i uke {n}', { name, h, n: wk })
          : actual
            ? T('{name} jobbet {h} timer {day}', { name, h, day: weekday })
            : T('{name} er planlagt {h} timer {day}', { name, h, day: weekday }),
      fig: {
        kind: 'meter',
        big: T('{h} t', { h }),
        legend: meter.over ? T('overtid') : actual ? T('stemplet') : T('planlagt'),
        value: meter.value,
        limit: meter.limit,
        marks: [{ at: meter.limit, label: T('{h} t', { h: num(meter.limit) }) }],
      },
      ...status,
      ...(e.code === 'year_over_200'
        ? { why: T('Mer enn 200 timer overtid på 52 uker er ikke lov.'), law: T('AML § 10-6 (6)') }
        : { why: ruleSaid }),
      ...(e.week ? {} : { actions: [openShift, ...daySeen] }),
    };
  }
  if ((e.code === 'rest' || e.code === 'youth_rest') && shift) {
    // the shift before, the person's latest that ended before this one starts
    const before = unique(shifts)
      .filter(
        (s) => s.user_id === shift.user_id && s.id !== shift.id && ms(s.end_time) <= ms(shift.start_time),
      )
      .sort((a, b) => ms(b.end_time) - ms(a.end_time))[0];
    const need = e.code === 'rest' ? 11 : n0(e.args.max) || 12;
    const rest = before ? round1((ms(shift.start_time) - ms(before.end_time)) / H) : null;
    return {
      ...at,
      ...status,
      what:
        rest != null
          ? T('{h} t mellom vaktene, {u} t under kravet', { h: num(rest), u: num(round1(need - rest)) })
          : ruleSaid,
      ...(before
        ? {
            facts: [
              fact(T('Forrige vakt slutter'), clock(before.end_time)),
              fact(T('Neste vakt starter'), clock(shift.start_time)),
            ],
          }
        : {}),
      ...(before && rest != null
        ? {
            fig: {
              kind: 'rest' as const,
              legend: T('Streken viser {n} t, det vil si til {t}', {
                n: need,
                t: hm(new Date(ms(before.end_time) + need * H).toISOString()),
              }),
              one: { ...chipOf(before), start: before.start_time, end: before.end_time },
              two: { ...chipOf(shift), start: shift.start_time, end: shift.end_time },
              need,
            },
          }
        : {}),
      ...(e.code === 'rest'
        ? {
            why: T('Planlagt mellomrom. Loven krever minst 11 timer sammenhengende fri i løpet av 24 timer.'),
            law: T('AML § 10-8 (1)'),
          }
        : { why: ruleSaid }),
      actions: [openShift, ...daySeen],
    };
  }
  if ((e.code === 'youth_night' || e.code === 'youth_evening') && shift) {
    const minutes = n0(e.args.minutes);
    // the group from the entry's own minutes: the window that gives them, on the plan or the punches
    const spans = [hoursOf(shift, true), hoursOf(shift, false)].filter((x): x is [number, number] => !!x);
    const match = (from: number, to: number) =>
      spans.find(([a, b]) => Math.abs(overlapMin(a, b, from, to) - minutes) <= 1);
    const teen = e.code === 'youth_evening' ? match(21, 23) : match(23, 30);
    const child = e.code === 'youth_night' && !teen ? match(20, 30) : undefined;
    const g = teen ? YOUTH.teen : child ? YOUTH.child : null;
    const span = teen ?? child;
    if (!g || !span) return { ...at, why: ruleSaid };
    const night = overlapMin(span[0], span[1], g.nightFrom, 30);
    const [wa, wb] =
      e.code === 'youth_evening'
        ? [Math.max(span[0], 21), Math.min(span[1], 23)]
        : [Math.max(span[0], g.nightFrom), Math.min(span[1], 30)];
    const hh = (h: number) =>
      `${String(Math.floor(h) % 24).padStart(2, '0')}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`;
    return {
      ...at,
      ...status,
      what:
        e.code === 'youth_evening'
          ? T('{from}–{to}, {n} min mellom 21 og 23', { from: hh(wa), to: hh(wb), n: minutes })
          : T('{from}–{to}, {n} min etter grensen', { from: hh(wa), to: hh(wb), n: night }),
      fig: {
        kind: 'youth',
        big: T('{n} min', { n: e.code === 'youth_evening' ? minutes : night }),
        legend:
          e.code === 'youth_evening'
            ? T('mellom 21 og 23, 15 til 17 år')
            : teen
              ? T('etter kl. 23, 15 til 17 år')
              : T('etter kl. 20, under 15 år eller skolepliktig'),
        shift: chipOf(shift),
        from: span[0],
        to: span[1],
        nightFrom: g.nightFrom,
        eveningFrom: g.eveningFrom,
      },
      why: teen
        ? T('For 15 til 17 år er arbeid mellom 23 og 06 ikke lov.')
        : T('Under 15 år, eller skolepliktig, er arbeid mellom 20 og 06 ikke lov.'),
      law: T(g.law),
      actions: [openShift, ...daySeen],
    };
  }
  if (
    (e.code === 'lunch_missing' || e.code === 'lunch_short') &&
    shift?.punch_in_time &&
    shift.punch_out_time
  ) {
    const pause = pauseOf(shift, Date.now()) ?? 0;
    const clocked = Math.round((ms(shift.punch_out_time) - ms(shift.punch_in_time)) / 60_000);
    return {
      ...at,
      ...status,
      what:
        e.code === 'lunch_missing'
          ? T('Ingen pause registrert')
          : T('Registrert pause {n} min', { n: pause }),
      facts: [
        fact(T('Stemplet'), `${hm(shift.punch_in_time)}–${hm(shift.punch_out_time)} (${duration(clocked)})`),
        fact(T('Registrert pause'), T('{n} min', { n: pause })),
        fact(T('Krav'), T('minst 30 min ved 8 t eller mer')),
      ],
      why: T(
        'Ingen registrert pause betyr ikke at pausen ikke ble tatt. Rett registreringen hvis pausen ble tatt.',
      ),
      law: T('AML § 10-9 (1)'),
      actions: [{ kind: 'shift', shiftId: shift.id, label: T('Rett pausen') }, ...contact(user, 'sms')],
    };
  }
  // the facts of these are not in the case today (trenger nye data): text only, the law said
  const wk = e.week ? isoWeek(e.week) : '';
  const words: Record<string, Template> = {
    weekrest: {
      what: T('{name} får ikke 35 timer fri i uke {n}', { name, n: wk }),
      why: T('Den ansatte skal ha 35 timer sammenhengende fri hver uke.'),
      law: T('AML § 10-8 (2)'),
    },
    sundays: {
      what: T('{name} jobber to søndager på rad', { name }),
      why: T('Den som har jobbet en søndag, skal ha fri neste søndag.'),
      law: T('AML § 10-8 (4)'),
    },
    age_unknown: {
      status: T(STATE.fix),
      what: T('Fødselsdato mangler for {name}', { name: fullName(user ?? shift?.user) }),
      why: T('Uten fødselsdato sjekkes ikke reglene for arbeidstakere under 18 år.'),
      law: T('AML kap. 11'),
      ...(user ? { actions: [{ kind: 'person', id: user.id, label: T('Åpne ansattkortet') }] } : {}),
    },
  };
  return { ...at, ...status, ...(words[e.code] ?? { why: ruleSaid }) };
}

export function weekCaseOf(
  it: WeekItem,
  users: ApiUser[],
  shifts: ApiShift[] = [],
  today = todayOslo(),
): ReviewCase | null {
  const cat = WEEK_CAT[it.group];
  if (!cat) return null;
  // who and when from the ids the item carries, never from its sentence: a shift item names its
  // shift, a week rule its person and week, a staffing item its day (review of PR #150, RRF1)
  const shift = it.shiftId != null ? shifts.find((s) => s.id === it.shiftId) : undefined;
  const personId = it.person ?? shift?.user_id ?? undefined;
  const user = personId != null ? users.find((u) => u.id === personId) : undefined;
  const date = it.day ?? (shift ? (oslo(shift.start_time)?.date ?? undefined) : undefined);
  const week = it.rule?.week;
  const label =
    it.shiftId != null
      ? T('Åpne vakten')
      : it.day
        ? T('Se dagen')
        : it.rule
          ? T('Se uka')
          : T('Se medarbeideren');
  const { actions, title, facts, ...parts } = weekTemplate(it, shift, user, shifts, today);
  return {
    ...parts,
    key: `wk-${it.key}`,
    cat,
    tone: it.sev,
    person: user ? { id: user.id, name: fullName(user) } : undefined,
    date,
    dateLabel: date ? day(date) : week ? T('Uke {n}', { n: isoWeek(week) }) : undefined,
    when: T('I perioden du ser'),
    title: title ?? it.text.charAt(0).toUpperCase() + it.text.slice(1),
    description: it.who,
    facts: facts ?? [],
    actions: actions ?? [{ kind: 'open', item: it.key, label }],
    shiftId: it.shiftId,
    span: shift ? { start: shift.start_time, end: shift.end_time } : undefined,
    records: undefined,
  };
}

/** What a case is about, as one issue: the kind and its subject. Two cases with one identity are one. */
export function identityOf(c: ReviewCase): string {
  if (c.key.startsWith('wk-')) return c.key;
  const kind = /^[a-z]+/.exec(c.key)?.[0] ?? c.key;
  return c.shiftId != null ? `${kind}:${c.shiftId}` : c.key;
}
const weekIdentity = (it: ReviewItem) =>
  it.group === 'rules' && it.shiftId != null && it.code ? `rule:${it.shiftId}:${it.code}` : `wk-${it.key}`;

/** Every case in the bar's order: the categories as designed, then breaches first, then oldest. */
export function casesOf(x: CaseInput): { cases: ReviewCase[]; waiting: ReviewCase[] } {
  const points = x.week?.thisWeek ? x.queue.points.filter((p) => p.key !== 'breaches') : x.queue.points;
  const all = points.map((p) => caseOf(p, x));
  // The same issue is said once; a different issue on the same shift is its own case (review of PR
  // #150, F1). Attendance and punch rules are records in the queue's month-close worklists and
  // today's rows, so the week's own copies are the same issue; requests and absences are the queue's.
  const held = new Set(all.map(identityOf));
  for (const it of x.week?.items ?? []) {
    if (it.group === 'attend' || it.group === 'requests' || it.group === 'absence') continue;
    if (it.code && (it.code.startsWith('punch_') || it.code === 'open')) continue;
    const id = weekIdentity(it);
    if (held.has(id)) continue;
    const c = weekCaseOf(it, x.users, [...x.shifts, ...(x.week?.shifts ?? [])], x.today);
    if (!c) continue;
    held.add(id);
    all.push(c);
  }
  const pay = x.payMonth ? payCase(x.payMonth) : null;
  if (pay) all.push(pay);
  const rank = (c: ReviewCase) => CATS.findIndex(([k]) => k === c.cat);
  const tone = (c: ReviewCase) => 'dwi'.indexOf(c.tone);
  const cases = all.sort(
    (a, b) => rank(a) - rank(b) || tone(a) - tone(b) || (a.date ?? '').localeCompare(b.date ?? ''),
  );
  return { cases, waiting: x.queue.waiting.map((p) => caseOf(p, x)) };
}

/** "1 nå · 3 forespørsler · 4 om timer · 1 om planen · 1 om lønn": the closed bar's breakdown (UC1). */
export function breakdown(cases: ReviewCase[]): string {
  const n = (c: CaseCat) => cases.filter((x) => x.cat === c).length;
  const parts: [CaseCat, string, string][] = [
    ['na', 'nå', 'nå'],
    ['foresporsler', 'forespørsel', 'forespørsler'],
    ['timer', 'om timer', 'om timer'],
    ['planlegg', 'om planen', 'om planen'],
    ['lonn', 'om lønn', 'om lønn'],
  ];
  return parts
    .filter(([c]) => n(c))
    .map(([c, one, many]) => `${n(c)} ${n(c) === 1 ? T(one) : T(many)}`)
    .join(' · ');
}

/** The queue's unanswered reads, and the pay month's, as gaps in the bar's categories (RF1). */
export function gapsOf(
  unsure: { label: string; groups: Group[]; state: 'loading' | 'failed' }[],
  pay: { enabled: boolean; isError: boolean; hasData: boolean; loading: boolean },
): SourceGap[] {
  const out: SourceGap[] = unsure.map((u) => ({
    label: u.label,
    cats: [...new Set(u.groups.map((g) => CAT_OF[g]))],
    state: u.state,
  }));
  if (pay.enabled && pay.isError && !pay.hasData)
    out.push({ label: T('lønn'), cats: ['lonn'], state: 'failed' });
  else if (pay.enabled && pay.loading) out.push({ label: T('lønn'), cats: ['lonn'], state: 'loading' });
  return out;
}

/**
 * A #146 popover's Gjennomgå, as the bar's cases (review of #147, F4). The popover names a shift's
 * entry `s<shift>-<n>`, as the bar's week items do, and a week rule `w<person>-<week>-<code>`, which
 * the bar holds as `wk-w<person><code>`. A shift entry the queue already holds (an open punch, a
 * cover) is that case. What is resolved since is simply not there; the caller says so.
 */
export function casesForKeys(keys: string[], cases: ReviewCase[]): string[] {
  const out = new Set<string>();
  for (const k of keys) {
    const shift = /^s(\d+)-\d+$/.exec(k);
    const week = /^w(\d+)-.*-([^-]+)$/.exec(k);
    const hit = shift
      ? (cases.find((c) => c.key === `wk-${k}`) ?? cases.find((c) => c.shiftId === Number(shift[1])))
      : week
        ? cases.find((c) => c.key === `wk-w${week[1]}${week[2]}`)
        : cases.find((c) => c.key === k);
    if (hit) out.add(hit.key);
  }
  return [...out];
}
